/**
 * Inline content diff.
 *
 * Paragraphs, headings, list items, and other inline containers are diffed
 * as flat token sequences rather than as nested HTML trees.  This lets
 * the algorithm detect format changes (bold → italic on the same word)
 * separately from content changes, producing cleaner diffs.
 */
import type { ChildNode, Element, Text } from 'domhandler';

import { MARK_RULES, isInlineAtomToken, getElementId, type DiffMark } from '../schema';
import { CHANGES_MATH_NODE_NAME } from '../parser/parser';
import { diffSequence } from './matcher';
import { cleanupCharDiff, type TCharRun } from './charDiffCleanup';
import { attrsEqual, marksEqual, normalizeStyleAttr, stableValue } from './compare';
import { MAX_CHAR_DIFF } from './constants';
import { TEXT_TOKEN_REGEX, isWholesaleRewrite, isWordText } from './wordTokens';
import type { TDiffNode, TTextDiffSegment } from '../types';

// ---------------------------------------------------------------------------
// Tokenization
// ---------------------------------------------------------------------------

type InlineToken =
  | { readonly kind: 'text'; readonly text: string; readonly marks: readonly DiffMark[] }
  | {
      readonly kind: 'atom';
      readonly element: Element;
      readonly signature: string;
      readonly marks: readonly DiffMark[];
    };

/**
 * Normalize whitespace runs to a single space so source-formatting
 * differences don't produce spurious diffs.
 */
const normalizeInlineTokenText = (text: string): string => (/^\s+$/u.test(text) ? ' ' : text);

const splitTextIntoTokens = (text: string): string[] =>
  (text.match(TEXT_TOKEN_REGEX) ?? (text ? [text] : [])).map(normalizeInlineTokenText);

/**
 * Stable signature for inline atoms so the token matcher can anchor on identity.
 * Preferred: type + id.  Fallback: type + stable attrs.
 */
const createAtomSignature = (el: Element): string => {
  const id = getElementId(el);
  if (id) return `${el.tagName}#${id}`;
  // Math atoms match by type alone so that formula changes produce
  // modifications (enabling char-level diff) rather than delete+insert.
  if (el.tagName === CHANGES_MATH_NODE_NAME) return el.tagName;
  return `${el.tagName}:${stableValue(normalizeStyleAttr({ ...el.attribs }))}`;
};

/**
 * Flatten the inline content of an element into a token sequence.
 *
 * The walker descends into mark wrappers (strong, em, span[data-custom-style], …)
 * accumulating marks.  Text nodes become lexical tokens.  Atom elements become
 * anchor tokens.  Unknown inline wrappers (e.g. <span class="caption-content">)
 * are walked through transparently.
 */
const flattenInline = (parent: Element): InlineToken[] => {
  const tokens: InlineToken[] = [];

  const walk = (node: ChildNode, marks: readonly DiffMark[]): void => {
    if (node.type === 'text') {
      const text = (node as Text).data;
      splitTextIntoTokens(text).forEach((token) => {
        if (token) tokens.push({ kind: 'text', text: token, marks });
      });
      return;
    }

    if (node.type !== 'tag') return;
    const el = node as Element;

    // Check if this element is a mark wrapper
    const markRule = MARK_RULES[el.tagName];
    if (markRule) {
      const mark = markRule(el.attribs);
      if (mark) {
        el.children.forEach((child) => walk(child, [...marks, mark]));
        return;
      }
    }

    // Check if this element is an inline atom
    // Atoms carry the accumulated marks from their wrapper elements.
    // E.g. <strong><ref ...></ref></strong> → ref atom has marks: [bold].
    // Uses isInlineAtomToken from schema - covers tag-based atoms + class-based
    // special elements (tabs, template field inline/dropdown wrappers).
    if (isInlineAtomToken(el)) {
      tokens.push({ kind: 'atom', element: el, signature: createAtomSignature(el), marks });
      return;
    }

    // Unknown inline element - walk children transparently with current marks
    el.children.forEach((child) => walk(child, marks));
  };

  parent.children.forEach((child) => walk(child, []));
  return tokens;
};

/**
 * Equality for the inline token LCS.
 *
 * Marks are intentionally ignored during matching so that e.g.
 * "Hello" in bold ↔ "Hello" in italic aligns as the same text,
 * and the format change is detected afterwards.
 */
const tokensEqual = (prevToken: InlineToken, currToken: InlineToken): boolean => {
  if (prevToken.kind !== currToken.kind) return false;
  if (prevToken.kind === 'text' && currToken.kind === 'text') return prevToken.text === currToken.text;
  if (prevToken.kind === 'atom' && currToken.kind === 'atom') return prevToken.signature === currToken.signature;
  return false;
};

// ---------------------------------------------------------------------------
// Wholesale-rewrite gate
// ---------------------------------------------------------------------------

// Whitespace and atoms are tokens too, and both match without proving any wording is shared:
// a space matches a space in any two paragraphs, and changes-math matches by tag alone.
const isWordToken = (token: InlineToken): boolean => token.kind === 'text' && isWordText(token.text);

// ---------------------------------------------------------------------------
// TDiffNode builders for inline text
// ---------------------------------------------------------------------------

const createTextNode = (segment: TTextDiffSegment): TDiffNode => ({
  type: 'text',
  change: segment.change === 'unchanged' ? 'unchanged' : 'modified',
  attrs: {},
  marks: segment.marks,
  prevMarks: segment.prevMarks,
  textSegments: [segment],
  isText: true,
  isInline: true,
});

const pushTextSegment = (children: TDiffNode[], segment: TTextDiffSegment): void => {
  if (!segment.text) return;

  // Merge with previous segment if it has the same change kind and marks
  const previousChild = children.at(-1);
  if (
    previousChild?.isText &&
    previousChild.textSegments?.length === 1 &&
    previousChild.textSegments[0].change === segment.change &&
    marksEqual(previousChild.textSegments[0].marks, segment.marks) &&
    marksEqual(previousChild.textSegments[0].prevMarks ?? [], segment.prevMarks ?? [])
  ) {
    // Mutation is safe here - we're building the TDiffNode, it becomes readonly after return
    (previousChild.textSegments[0] as { text: string }).text += segment.text;
    return;
  }

  children.push(createTextNode(segment));
};

const pushInsertedText = (children: TDiffNode[], token: Extract<InlineToken, { kind: 'text' }>): void => {
  pushTextSegment(children, { text: token.text, change: 'inserted', marks: token.marks });
};

const pushDeletedText = (children: TDiffNode[], token: Extract<InlineToken, { kind: 'text' }>): void => {
  pushTextSegment(children, { text: token.text, change: 'deleted', marks: token.marks });
};

const pushMatchedText = (
  children: TDiffNode[],
  currToken: Extract<InlineToken, { kind: 'text' }>,
  prevToken: Extract<InlineToken, { kind: 'text' }>
): void => {
  pushTextSegment(children, {
    text: currToken.text,
    change: marksEqual(currToken.marks, prevToken.marks) ? 'unchanged' : 'format',
    marks: currToken.marks,
    prevMarks: marksEqual(currToken.marks, prevToken.marks) ? undefined : prevToken.marks,
  });
};

const emitReplacedTokens = (
  children: TDiffNode[],
  delTokens: readonly InlineToken[],
  insTokens: readonly InlineToken[],
  buildInserted: (el: Element) => TDiffNode,
  buildDeleted: (el: Element) => TDiffNode
): void => {
  for (const token of delTokens) {
    if (token.kind === 'text') pushDeletedText(children, token);
    else children.push({ ...buildDeleted(token.element), marks: token.marks });
  }
  for (const token of insTokens) {
    if (token.kind === 'text') pushInsertedText(children, token);
    else children.push({ ...buildInserted(token.element), marks: token.marks });
  }
};

// ---------------------------------------------------------------------------
// Character-level refinement
// ---------------------------------------------------------------------------

type TTextToken = Extract<InlineToken, { kind: 'text' }>;

/** Per-character mark map so each character preserves its original marks. */
const buildCharMarkMap = (tokens: TTextToken[]): DiffMark[][] => {
  const map: DiffMark[][] = [];
  for (const token of tokens) {
    for (const _char of [...token.text]) {
      map.push([...token.marks]);
    }
  }
  return map;
};

/** Cleanup may shift edits without misplacing marks only when one side has the same marks all the way through. */
const sameMarksThroughout = (markMap: DiffMark[][]): boolean => markMap.every((marks) => marksEqual(marks, markMap[0]));

// Delete+equal runs walk the old text in order and insert+equal walk the new, so one index
// per side (delIndex/insIndex) stays lined up with its per-character mark map.
const emitCleanedCharRuns = (
  runs: TCharRun[],
  delMarkMap: DiffMark[][],
  insMarkMap: DiffMark[][],
  children: TDiffNode[]
): void => {
  let delIndex = 0;
  let insIndex = 0;
  for (const run of runs) {
    if (run.op === 'equal') {
      for (const char of run.chars) {
        const currMarks = insMarkMap[insIndex];
        const prevMarks = delMarkMap[delIndex];
        const marksChanged = !marksEqual(currMarks, prevMarks);
        pushTextSegment(children, {
          text: char,
          change: marksChanged ? 'format' : 'unchanged',
          marks: currMarks,
          prevMarks: marksChanged ? prevMarks : undefined,
        });
        delIndex += 1;
        insIndex += 1;
      }
    } else if (run.op === 'insert') {
      for (const char of run.chars) {
        pushTextSegment(children, { text: char, change: 'inserted', marks: insMarkMap[insIndex] });
        insIndex += 1;
      }
    } else {
      for (const char of run.chars) {
        pushTextSegment(children, { text: char, change: 'deleted', marks: delMarkMap[delIndex] });
        delIndex += 1;
      }
    }
  }
};

/** Count the separate changed stretches: each starts at an edit that has no edit right before it. */
const countEditIslands = (runs: TCharRun[]): number =>
  runs.filter((run, index) => run.op !== 'equal' && (index === 0 || runs[index - 1].op === 'equal')).length;

/** Refine adjacent delete+insert text runs into character-level diffs (like MS Word). */
const refineCharLevel = (delTokens: TTextToken[], insTokens: TTextToken[], children: TDiffNode[]): void => {
  const delText = delTokens.map((t) => t.text).join('');
  const insText = insTokens.map((t) => t.text).join('');
  const delChars = [...delText];
  const insChars = [...insText];

  const emitWholeTokens = (): void => {
    delTokens.forEach((t) => pushDeletedText(children, t));
    insTokens.forEach((t) => pushInsertedText(children, t));
  };

  // Guard against O(m*n) blow-up on very long paragraph rewrites
  if (delChars.length > MAX_CHAR_DIFF || insChars.length > MAX_CHAR_DIFF) {
    emitWholeTokens();
    return;
  }

  const delMarkMap = buildCharMarkMap(delTokens);
  const insMarkMap = buildCharMarkMap(insTokens);

  // A partly-marked run keeps the exact LCS pairing; cleanup may shift edits only when marks are uniform.
  const allowShift = sameMarksThroughout(delMarkMap) && sameMarksThroughout(insMarkMap);
  const charOps = diffSequence(delChars, insChars, (a, b) => a === b);
  const runs = cleanupCharDiff(charOps, allowShift);

  // One token swapped for one token reads well with a single changed stretch (12<del>4</del><ins>5</ins>),
  // but edits on both sides of a kept character (0.0<del>7</del>8<ins>0</ins>) read as a different value.
  if (delTokens.length === 1 && insTokens.length === 1 && countEditIslands(runs) > 1) {
    emitWholeTokens();
    return;
  }

  emitCleanedCharRuns(runs, delMarkMap, insMarkMap, children);
};

const buildInlineContainerNode = (currElement: Element, prevElement: Element, children: TDiffNode[]): TDiffNode => {
  const attrsChanged = !attrsEqual(currElement.tagName, { ...currElement.attribs }, { ...prevElement.attribs });
  const typeChanged = currElement.tagName !== prevElement.tagName;
  const hasChanges = children.some((child) => child.change !== 'unchanged');

  return {
    type: currElement.tagName,
    change: attrsChanged || typeChanged || hasChanges ? 'modified' : 'unchanged',
    attrs: { ...currElement.attribs },
    prevAttrs: attrsChanged ? { ...prevElement.attribs } : undefined,
    marks: [],
    children,
    isInline: false, // The container itself (p, h2, li) is a block element
    currElement,
    prevElement,
  };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Diff the inline content of two elements (paragraphs, headings, list items, …).
 *
 * Algorithm:
 * 1. Flatten both sides into deterministic token sequences.
 * 2. Run exact LCS on the token sequences.
 * 3. Convert the edit script into DiffNodes:
 *    - equal text → unchanged or format change (marks compared after alignment)
 *    - equal atom → recurse with diffNode
 *    - insert/delete → corresponding TDiffNode
 * 4. Adjacent delete+insert text runs are refined with character-level LCS.
 */
export const diffInlineContent = (
  currElement: Element,
  prevElement: Element,
  diffNode: (curr: Element, prev: Element) => TDiffNode,
  buildInserted: (el: Element) => TDiffNode,
  buildDeleted: (el: Element) => TDiffNode
): TDiffNode => {
  const currTokens = flattenInline(currElement);
  const prevTokens = flattenInline(prevElement);
  const operations = diffSequence(prevTokens, currTokens, tokensEqual);
  const children: TDiffNode[] = [];

  if (isWholesaleRewrite(operations, isWordToken)) {
    emitReplacedTokens(children, prevTokens, currTokens, buildInserted, buildDeleted);
    return buildInlineContainerNode(currElement, prevElement, children);
  }

  let i = 0;
  while (i < operations.length) {
    const operation = operations[i];

    if (operation.type === 'equal') {
      const { currItem: currToken, prevItem: prevToken } = operation;

      if (currToken.kind === 'text' && prevToken.kind === 'text') {
        pushMatchedText(children, currToken, prevToken);
      } else if (currToken.kind === 'atom' && prevToken.kind === 'atom') {
        const atomDiff = diffNode(currToken.element, prevToken.element);
        const atomMarksChanged = !marksEqual(currToken.marks, prevToken.marks);
        children.push({
          ...atomDiff,
          marks: currToken.marks,
          prevMarks: atomMarksChanged ? prevToken.marks : atomDiff.prevMarks,
          change: atomMarksChanged && atomDiff.change === 'unchanged' ? 'modified' : atomDiff.change,
        });
      }
      i += 1;
      continue;
    }

    // Collect consecutive delete/insert run
    const delTokens: InlineToken[] = [];
    const insTokens: InlineToken[] = [];
    while (i < operations.length && operations[i].type !== 'equal') {
      const op = operations[i];
      if (op.type === 'delete') delTokens.push(op.prevItem);
      else insTokens.push(op.currItem);
      i += 1;
    }

    // Refine with char-level LCS when both sides are pure text
    const allDelText = delTokens.every((t) => t.kind === 'text');
    const allInsText = insTokens.every((t) => t.kind === 'text');
    if (allDelText && allInsText && delTokens.length > 0 && insTokens.length > 0) {
      refineCharLevel(delTokens as TTextToken[], insTokens as TTextToken[], children);
      continue;
    }

    // Emit non-refinable tokens as-is
    emitReplacedTokens(children, delTokens, insTokens, buildInserted, buildDeleted);
  }

  return buildInlineContainerNode(currElement, prevElement, children);
};
